import React, { useId } from 'react';

import { Tooltip } from 'antd';
import {
  STEP_SELECTOR_CANCEL_LABEL,
  STEP_SELECTOR_SAVE_LABEL,
} from 'constants/uiConstants';
import MergeClassNames from 'utils/MergeClassNames';

interface SelectionActionBarProps {
  isSaveDisabled: boolean;
  /**
   * Why Save can't run yet (e.g. unconfirmed flyout edits). Save stays focusable but marked
   * unavailable, with the reason as a hover / focus tooltip and announced to screen readers.
   */
  saveBlockedReason?: string;
  onCancel: () => void;
  onSave: () => void;
}

/** Multi-select Cancel / Save bar at the foot of the step picker. */
const SelectionActionBar: React.FC<SelectionActionBarProps> = ({
  isSaveDisabled,
  saveBlockedReason,
  onCancel,
  onSave,
}) => {
  const reasonId = useId();
  const blockedReason = isSaveDisabled ? undefined : saveBlockedReason;

  return (
    <div className="mt-3 flex items-center justify-end gap-2">
      <button
        type="button"
        className="rounded-[var(--radius-button)] border border-gray-medium bg-white px-3 py-1.5 text-sm font-medium text-charcoal"
        onClick={onCancel}
      >
        {STEP_SELECTOR_CANCEL_LABEL}
      </button>
      <Tooltip title={blockedReason} trigger={['hover', 'focus']}>
        {/* span: antd Tooltip needs a wrapper that still gets events from an unavailable button */}
        <span className="inline-flex">
          <button
            type="button"
            className={MergeClassNames(
              'rounded-[var(--radius-button)] bg-success-dark px-3 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60',
              blockedReason && 'cursor-not-allowed opacity-60'
            )}
            disabled={isSaveDisabled}
            aria-disabled={blockedReason ? true : undefined}
            aria-describedby={blockedReason ? reasonId : undefined}
            onClick={onSave}
          >
            {STEP_SELECTOR_SAVE_LABEL}
          </button>
        </span>
      </Tooltip>
      {blockedReason && (
        <span id={reasonId} className="sr-only">
          {blockedReason}
        </span>
      )}
    </div>
  );
};

export default SelectionActionBar;
